import type { EventBus } from "../core/EventBus.ts";
import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { Projectile } from "../entities/Projectile.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import type { SynergySystem } from "./SynergySystem.ts";
import { isTanky } from "./combatUtils.ts";
import type {
  Element,
  SpecialEffect,
  VirusSynergyMods,
} from "../entities/types.ts";
import { isStealthHidden } from "./StealthGuard.ts";
import { isImmuneTo } from "./EnemyImmunity.ts";
import { COMBAT, TOWER_CAPABILITY, GOLD_TIER } from "../core/config.ts";
import { virusInfectionProfile } from "./VirusLevels.ts";
import type { VirusProfile } from "./VirusLevels.ts";
import { infectEnemiesInRadius } from "./VirusInfect.ts";

/**
 * Damage calculation and application logic.
 * Handles multipliers, special effects (DoT, chain, burst), and synergy stun.
 */

/** APEX_PREDATOR per-element modifier (BL-72): dominant element boosted, the
 *  rest penalized. Computed once per frame from live towers in CombatSystem. */
export interface ApexMod {
  dominant: string;
  boost: number;
  penalty: number;
}

export interface DamageContext {
  draftSystem: DraftSystem | null;
  synergySystem: SynergySystem | null;
  elementBonuses: Record<string, number>;
  towers: Tower[];
  enemies: Enemy[];
  bus: EventBus;
  bossDamageMult?: number;
  globalDamageMult?: number;
  apex?: ApexMod | null;
  rng: () => number;
}

export interface HazardPoolSpawn {
  x: number;
  y: number;
  profile: VirusProfile;
}

/** Calculate final damage with all multipliers applied. */
export function calculateDamage(
  baseDmg: number,
  element: Element,
  draftSystem: DraftSystem | null,
  elementBonuses: Record<string, number>,
  globalDamageMult = 1,
  apex: ApexMod | null = null,
): number {
  let multiplier = 1;
  if (draftSystem) {
    multiplier *=
      draftSystem.getDamageMultiplier(element) *
      draftSystem.getGlobalDamageMultiplier();
  }
  const elBonus = elementBonuses[element];
  if (elBonus !== undefined) {
    multiplier *= elBonus;
  }
  // APEX_PREDATOR: dominant element boosted (+120%), all others penalized (−40%).
  // Null when no APEX card is active, so neutral for the deterministic baseline.
  if (apex) {
    multiplier *= element === apex.dominant ? apex.boost : apex.penalty;
  }
  // globalDamageMult carries BOTH the static channel (glass/scorched/quantum)
  // AND the per-frame dynamic card stack (minimalist × resonance × dead-man's ×
  // greed-penalty) folded in by CombatSystem — so those flow through here too.
  multiplier *= globalDamageMult;
  return Math.round(baseDmg * multiplier);
}

/** Apply projectile damage to target, including stun and special effects. */
export function applyProjectileDamage(
  proj: Projectile,
  ctx: DamageContext,
): void {
  // hasLiveTarget() rejects both dead targets and pool-recycled slots whose
  // version no longer matches the one captured at fire time.
  if (!proj.hasLiveTarget()) return;
  const target = proj.target!;

  let dmg = calculateDamage(
    proj.damage,
    proj.element,
    ctx.draftSystem,
    ctx.elementBonuses,
    ctx.globalDamageMult ?? 1,
    ctx.apex ?? null,
  );

  // Mark bonus (SIGNAL tower debuff)
  if (target.marked) {
    dmg = Math.round(dmg * (1 + target.markDamageBonus));
  }

  // LD-22.1-1 (post-hotfix): The previous stealth-50%-damage halving
  // is now dead code — unrevealed stealth can no longer be targeted
  // (Targeting.ts:findTarget early-continues). On reveal, full damage
  // applies like any normal enemy. No halving needed.

  // Boss/Tank/Golem damage multiplier (zero_day_exploit / cold_boot)
  if (isTanky(target) && ctx.bossDamageMult !== undefined) {
    dmg = Math.round(dmg * ctx.bossDamageMult);
  }

  // Burst crit: multiply damage before application (M2-F2)
  if (
    proj.special?.type === "burst" &&
    ctx.rng() < (proj.special.critChance ?? 0.2)
  ) {
    dmg = Math.round(dmg * (proj.special.critMultiplier ?? 2));
  }

  const killed = target.takeDamage(dmg, proj.element);

  // Synergy stun check (Electroshock, GA-23: rolled on every damage-dealing
  // hit that lands within an Electroshock tower's range. This direct-impact
  // call site is element-agnostic — no proj.element check here; only the
  // chain-bounce call site below (chainHit) gates the roll to lightning
  // damage).
  if (!killed) {
    rollElectroshockStun(proj.x, proj.y, target, ctx);
  }

  if (proj.special) {
    applySpecial(proj.special, target, proj.element, dmg, proj.level, ctx);
  }

  if (killed) target.active = false;

  ctx.bus.emit("projectile.hit", { projectile: proj, target, damage: dmg });

  // BL-174 / D-263 — fire's "Scorch" splash (level-gated). After the primary
  // hit, a fraction of the dealt damage washes over every OTHER live foe within
  // the splash radius — fire's real anti-swarm AoE, the wiring of the long-dead
  // `fire.splash` field (BL-179). Self-contained (no event feedback into the
  // sim); splash kills route through the same WaveManager death scan as any kill.
  applyFireSplash(proj, dmg, ctx);

  // BL-175 / D-266 — fire's GOLD "HELIOS · Combustion": a GOLD fire kill DETONATES
  // the corpse (a NEW death-trigger). Run AFTER Scorch so a splash that itself
  // killed has already deactivated; the chain of detonations is bounded by the
  // one-pass enemy scan (a detonation can kill, but those corpses don't re-detonate
  // this frame — no unbounded recursion). Inert below GOLD / for non-fire.
  if (killed) applyHeliosCombustion(proj, target, ctx);
}

/** GA-23 (D-321): the Electroshock (water+lightning) synergy's stun roll,
 *  shared by every damage-dealing hit that lands within an Electroshock
 *  tower's range. The direct projectile-impact call site
 *  (applyProjectileDamage) is element-agnostic — no lightning check there;
 *  only the chain-bounce call site (chainHit below) gates the roll to
 *  lightning-element chain hits. Same stunChance/stunDuration data as the
 *  synergy definition (`SynergySystem.getStunChance`); no new constants. */
function rollElectroshockStun(
  x: number,
  y: number,
  target: Enemy,
  ctx: DamageContext,
): void {
  if (!ctx.synergySystem) return;
  for (const tower of ctx.towers) {
    const stunChance = ctx.synergySystem.getStunChance(tower);
    if (stunChance > 0) {
      const dx = tower.x - x;
      const dy = tower.y - y;
      if (dx * dx + dy * dy <= tower.range * tower.range) {
        if (ctx.rng() < stunChance) {
          target.applySlow(0, 800);
          // F-14: surface the Electroshock stun so the VFX layer can flash
          // the target — otherwise the stun "just happens" invisibly.
          ctx.bus.emit("synergy.stun", { target, tower });
        }
        break;
      }
    }
  }
}

/** BL-175 / D-266 (S170) — fire's GOLD signature "HELIOS · Combustion". When the
 *  GOLD fire tower lands a KILL, the corpse detonates for `goldCombustionFraction`
 *  of its OWN max-hp as fire-AoE over `goldCombustionRadius` px — kills seed kills.
 *  A brand-new death-trigger (NOT the Scorch splash, which is a fraction of the
 *  HIT). Gated on the carried projectile level reaching GOLD + the `gold*` data, so
 *  it has NO surface in any fixture (no fixture reaches L6) → byte-identical. */
function applyHeliosCombustion(
  proj: Projectile,
  corpse: Enemy,
  ctx: DamageContext,
): void {
  const s = proj.special;
  if (
    !s ||
    proj.level < GOLD_TIER.LEVEL ||
    s.goldCombustionFraction === undefined ||
    s.goldCombustionRadius === undefined
  ) {
    return;
  }
  const blastDmg = Math.round(corpse.maxHp * s.goldCombustionFraction);
  if (blastDmg <= 0) return;
  const rSq = s.goldCombustionRadius * s.goldCombustionRadius;
  for (const enemy of ctx.enemies) {
    if (!enemy.active || enemy === corpse) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - corpse.x;
    const dy = enemy.y - corpse.y;
    if (dx * dx + dy * dy > rSq) continue;
    const killed = enemy.takeDamage(blastDmg, proj.element);
    ctx.bus.emit("projectile.hit", {
      projectile: {
        x: enemy.x,
        y: enemy.y,
        element: proj.element,
        color: proj.color,
      },
      target: enemy,
      damage: blastDmg,
    });
    if (killed) enemy.active = false;
  }
}

/** Fire's level-gated "Area Burn" splash arc. No-op for any tower whose `special`
 *  carries no `splashRadius` (every non-fire tower) or below the unlock gate — so
 *  it is byte-identical for the early L1/L2 fixtures.
 *
 *  The arc, all gated on the carried projectile level (byte-safe — the harness
 *  places no bare-three `special`, so no fixture has a surface, D-263):
 *   • L3 "Scorch"    — the splash itself (shipped S166).
 *   • L4 "Wildfire"  — every splash-hit foe ALSO catches the DoT (the burn
 *                      PROPAGATES), so the AoE leaves lingering damage, not just
 *                      an impact (`special.wildfire`).
 *   • L5 "Firestorm" — the splash widens (radius ×) and bites harder (fraction ×),
 *                      a hotter, larger burn (`special.firestorm*Mult`).
 */
function applyFireSplash(
  proj: Projectile,
  hitDamage: number,
  ctx: DamageContext,
): void {
  const s = proj.special;
  if (
    !s ||
    s.splashRadius === undefined ||
    s.splashFraction === undefined ||
    proj.level < TOWER_CAPABILITY.UNLOCK_LEVEL
  ) {
    return;
  }

  // L5 "Firestorm": the same splash, widened + hotter. Gated strictly above L4 so
  // L3/L4 are unaffected; absent mults ⇒ ×1 (the byte-safe shipped splash).
  const firestorm = proj.level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 2;
  const radius = firestorm
    ? s.splashRadius * (s.firestormRadiusMult ?? 1)
    : s.splashRadius;
  const fraction = firestorm
    ? s.splashFraction * (s.firestormFractionMult ?? 1)
    : s.splashFraction;

  const splashDmg = Math.round(hitDamage * fraction);
  if (splashDmg <= 0) return;

  // L4 "Wildfire": the splash also spreads the burn (the DoT) to each foe it
  // washes over. Gated at L4+; the magnitudes reuse the tower's own DoT fields.
  const wildfire =
    proj.level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 1 && s.wildfire === true;

  const primary = proj.target;
  const cx = primary ? primary.x : proj.x;
  const cy = primary ? primary.y : proj.y;
  const rSq = radius * radius;

  for (const enemy of ctx.enemies) {
    if (!enemy.active || enemy === primary) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - cx;
    const dy = enemy.y - cy;
    if (dx * dx + dy * dy > rSq) continue;

    const killed = enemy.takeDamage(splashDmg, proj.element);
    if (!killed && wildfire) {
      enemy.applyDot(
        s.tickDamage ?? 5,
        s.duration ?? 3000,
        s.tickInterval ?? 500,
        proj.element,
      );
    }
    ctx.bus.emit("projectile.hit", {
      projectile: {
        x: enemy.x,
        y: enemy.y,
        element: proj.element,
        color: proj.color,
      },
      target: enemy,
      damage: splashDmg,
    });
    if (killed) enemy.active = false;
  }
}

export function applyInfectionZone(
  x: number,
  y: number,
  special: SpecialEffect | null,
  ctx: { enemies: Enemy[]; bus: EventBus; virusMods?: VirusSynergyMods | null },
  level = 1,
): HazardPoolSpawn | null {
  if (!special) return null;

  const p = virusInfectionProfile(level);
  // BL-175 / D-266 — a GOLD virus impact tags its infections for the "PANDEMIC"
  // death-burst (spore re-infection on the infected foe's death). `undefined`
  // below GOLD / without the data ⇒ untagged (byte-safe; no fixture is GOLD).
  const goldSpore =
    level >= GOLD_TIER.LEVEL ? special.goldSporeRadius : undefined;
  const infected = infectEnemiesInRadius(
    x,
    y,
    p,
    ctx.enemies,
    ctx.virusMods,
    goldSpore,
  );

  ctx.bus.emit("virus.impact", {
    x,
    y,
    radius: p.zoneRadius,
    infected,
  });

  return p.hazardPoolDuration > 0 ? { x, y, profile: p } : null;
}

/** Apply special effect from projectile to target. */
export function applySpecial(
  special: SpecialEffect,
  target: Enemy,
  element: Element,
  baseDamage: number,
  level: number,
  ctx: DamageContext,
): void {
  switch (special.type) {
    case "dot": {
      // CR-H8 (LD-22.1-5): "Burning Mark" Signal+Fire synergy — if active
      // AND target is marked (by Signal tower), double DOT tick damage
      // AND duration. Closes the audit-noted Signal+Fire synergy gap.
      const boost =
        target.marked && ctx.synergySystem
          ? ctx.synergySystem.getMarkedDotBoost()
          : null;
      const tickMult = boost?.tickMult ?? 1;
      const durMult = boost?.durationMult ?? 1;
      target.applyDot(
        (special.tickDamage ?? 5) * tickMult,
        (special.duration ?? 3000) * durMult,
        special.tickInterval ?? 500,
        element,
      );
      if (boost)
        ctx.bus.emit("synergy.triggered", {
          synergy: { id: "burning_mark" },
          target,
          type: "burning_mark",
        });
      break;
    }
    case "chain":
      chainHit(baseDamage, special, target, element, level, ctx);
      break;
    case "burst":
      // Burst crit is now handled in applyProjectileDamage before takeDamage
      break;
  }
}

function chainHit(
  baseDamage: number,
  special: SpecialEffect,
  originTarget: Enemy,
  element: Element,
  level: number,
  ctx: DamageContext,
): void {
  // BL-174 / D-263 — water's "Cryo-Chain": at the unlock gate every bounced foe
  // is slowed, giving water a real late-game control niche (§H2). Off below the
  // gate / for any chain special without `chainSlowFactor` (byte-safe).
  const cryoChain =
    level >= TOWER_CAPABILITY.UNLOCK_LEVEL &&
    special.chainSlowFactor !== undefined;

  // BL-182 / D-265 (S169) — water's "Control / Freeze" L4/L5 arc, both gated on
  // level + their own `special` field (byte-safe: no fixture carries them).
  //   • L4 "Deep-Chill"  — the chain net deepens by `deepChillBounce` extra hops.
  //   • L5 "Flash-Freeze" — the PRIMARY target is fully stopped (slow ×0) briefly.
  const deepChill =
    level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 1
      ? (special.deepChillBounce ?? 0)
      : 0;
  if (
    level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 2 &&
    special.flashFreezeMs !== undefined
  ) {
    originTarget.applySlow(0, special.flashFreezeMs);
  }

  const chainRangeSq = COMBAT.CHAIN_RANGE * COMBAT.CHAIN_RANGE;
  let current: Enemy = originTarget;
  const totalBounces = (special.bounces ?? 2) + deepChill;
  for (let bounce = 0; bounce < totalBounces; bounce++) {
    let nearest: Enemy | null = null;
    let nearestDistSq = Infinity;

    for (const enemy of ctx.enemies) {
      if (!enemy.active || enemy === current) continue;
      if (isStealthHidden(enemy)) continue;
      if (isImmuneTo(enemy, "chain")) continue;
      const dx = current.x - enemy.x;
      const dy = current.y - enemy.y;
      const distSq = dx * dx + dy * dy;
      if (distSq < chainRangeSq && distSq < nearestDistSq) {
        nearest = enemy;
        nearestDistSq = distSq;
      }
    }
    if (!nearest) break;

    const chainDamage = Math.round(
      baseDamage * Math.pow(special.damageDecay ?? 0.6, bounce),
    );
    const killed = nearest.takeDamage(chainDamage, element);
    if (cryoChain) {
      nearest.applySlow(special.chainSlowFactor!, special.chainSlowMs ?? 1000);
    }
    // GA-23 (D-321): a lightning chain bounce is itself lightning damage
    // landing on a hit target — it rolls Electroshock the same as a direct
    // projectile impact, gated on the SAME bounce position/target so a
    // water+lightning pairing can stun mid-chain, not only on the first hit.
    if (!killed && element === "lightning") {
      rollElectroshockStun(current.x, current.y, nearest, ctx);
    }
    ctx.bus.emit("projectile.hit", {
      projectile: {
        x: current.x,
        y: current.y,
        element,
        color: "#00CED1",
      },
      target: nearest,
      damage: chainDamage,
    });
    if (killed) {
      nearest.active = false;
      break;
    }
    current = nearest;
  }
}
